<template>
  <div class="products-list-page">
    <!-- 页面标题区域 -->
    <div class="page-header"  :style="{ backgroundImage: `url('${bannerBg}')` }">
      <div class="header-bg"></div>
      <div class="container">
        <h1 class="page-title" style="font-size: 48px;">{{ $t('products.centerTitle') }}</h1>
        <div class="search-container">
          <div class="search-box">
            <el-input
                v-model="searchKeyword"
                @keyup.enter.native="handleSearch"
                :placeholder="$t('products.searchPlaceholder')"
                class="search-input">
              <template #prefix>
                <i class="el-icon-search"></i>
              </template>
              <template #suffix>
                <el-button type="primary" class="search-btn btn btn--rounded" @click="handleSearch">
                  {{ $t('products.searchBtn') }}
                </el-button>
              </template>
            </el-input>
          </div>
        </div>
      </div>
    </div>

    <!-- 酶类产品展示组件-->
    <BRList v-if="category===1" @getSelectedCategory="getSelectedCategoryId" :parentId="parentId" :current-page="currentPage" @pageChange="handlePageChange" :selectedCategory="selectedCategory" :total="total" :products="filteredProducts"></BRList>

    <!-- 化学试剂列表展示组件-->
    <CRList v-else @getSelectedCategory="getSelectedCategoryId" :current-page="currentPage" @pageChange="handlePageChange" :selectedCategory="selectedCategory" :total="total" :products="filteredProducts"></CRList>
  </div>
</template>

<script>
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { getProductPageList } from '@/api/products'
import {mapState} from "vuex";
import Pagination from '@/components/Pagination.vue'
import BRList from './BiochemicalReagent.vue'
import CRList from './ChemicalReagent.vue'
import {getEnzyme} from "@/api/menu";
import {pushWithLanguage} from "@/utils/routeUtils";
import { metaMixin } from "@/utils/metaUtils";

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'ProductsList',
  mixins: [metaMixin],
  components: {
    Pagination,
    BRList,
    CRList
  },

  data() {
    return {
      bannerBg: 'https://file.yacoo.com.cn/staticResource/products/products-header-bg.webp?x-oss-process=image/resize,h_480',
      searchKeyword: '',
      categories: [], // 所有分类数据
      selectedCategory: '',
      category: null,
      categoryInfo: null,
      currentPage: 1,
      pageSize: 10,
      total: 100,
      loading: false,
      products: [],
      parentId: '',
      categoryList: [],
      productQuery: {
        productId: '',
        name: '',
        parentId: '',
        parentName: ''
      }
    }
  },

  computed: {
    filteredProducts() {
      let result = this.products
      return result
    },
    ...mapState({
      currentLanguage: state => state.app.language
    })
  },
  watch: {
    // 监听路由
    $route: {
      handler() {
        this.getCategoryList(this.$route.params.id)
        this.$setMetaFromMenuItems()
      },
      immediate: true
    },
    selectedCategory: {
      handler(newVal, oldVal) {
        // 监听分类变化，重新加载产品列表
        if (newVal) {
          this.loadProducts()
        }
      }
    },
    // 监听语言变化
    currentLanguage: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          // 加载产品数据
          this.loadProducts()
          this.$setMetaFromMenuItems(newVal)
        }
      },
      immediate: false
    }
  },

  created() {
    this.getCategoryList(this.$route.params.id)
  },

  mounted() {
    // 设置 metaInfo
    this.$setMetaFromMenuItems()
    this.initAnimations()
  },

  metaInfo() {
    // 从 store 中获取 metaInfo，用于预渲染
    const metaInfo = this.$store.state.metaInfo || {}
    return {
      title: metaInfo.title || '产品列表_亚科股份',
      meta: [
        { name: "keywords", content: metaInfo.keywords || '产品列表、亚科股份' },
        { name: "description", content: metaInfo.description || '亚科股份产品列表' },
      ]
    }
  },

  methods: {
    //获取子组件 SelectedCategory
    getSelectedCategoryId(id) {
      this.selectedCategory = id
      this.currentPage = 1
      this.loadProducts()
    },
    // 分页
    handlePageChange(e){
      this.currentPage = e
      this.loadProducts()
    },
    handleSearch() {
      // 添加搜索功能
      if (this.searchKeyword.trim()) {
        pushWithLanguage('/ProductsSearch', this.currentLanguage, { keyword: this.searchKeyword }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
      }
    },

     loadProducts() {
      if(Number(this.category) === 1){
        this.getBrData()
      }else{
        this.getCrData()
      }
    },


    // 判断函数
    isInCategoryList(id) {
      const whitelist = this.categoryList
      return whitelist.includes(id);
    },


    //获取生物试剂的列表
    async getCategoryList(productId) {
      const res = await getEnzyme()
        if (res.success) {
          this.categoryList = res.data
          this.selectedCategory = productId
          if (this.isInCategoryList(this.selectedCategory)) {
            this.parentId = this.$route.params.id
            this.category = 1
          } else {
            this.category = null
          }
        } else {
          this.$message.error(res.msg)
        }
    },


    //化学世界数据处理
    async getCrData() {
      this.loading = true
      try {
        // 构建查询参数
        const params = {
          productTypeId: this.selectedCategory,
          keyword: this.searchKeyword
        }

        const response = await getProductPageList(params, {
          current: this.currentPage,
          size: this.pageSize
        })

        if (response.success) {
          this.products = response.data.records
          this.total = response.data.total
        } else {
          this.$message.error(response.msg || '获取产品列表失败')
        }
      } catch (error) {
        console.error('加载产品失败:', error)
        this.$message.error('加载产品失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },


    //酶类数据处理
    async getBrData() {
        this.loading = true
        try {
          // 构建查询参数
          const params = {
            productTypeId: this.selectedCategory,
            category: this.productQuery.category,
            keyword: this.searchKeyword
          }
          const response = await getProductPageList(params, {
            current: this.currentPage,
            size: this.pageSize
          })

          if (response.success) {
            this.products = response.data.records
            // this.module = this.products?this.products.filter(item => item.module? item : null)[0].module.content : []//获取包含module的对象
            this.total = response.data.total
          } else {
            this.$message.error(response.msg || '获取产品列表失败')
          }
        } catch (error) {
          console.error('加载产品失败:', error)
        } finally {
          this.loading = false
        }

    },

    handleProductClick(product) {
      pushWithLanguage(`/products/${product.id}`, this.currentLanguage).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error( err)
        }
      })
    },

    initAnimations() {
      // 页面标题动画
      gsap.from('.page-title, .breadcrumb', {
        y: 30,
        opacity: 0,
        duration: 1,
        stagger: 0.2,
        ease: 'power3.out'
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/assets/styles/variables.less';
@import '~@/assets/styles/mixins.less';

.products-list-page {
  .page-header {
    height: 480px;
    .container {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    .search-container {
      width: 100%;
      display: flex;
      justify-content: center;
      padding-top: 20px;
      .search-box {
        width: 100%;
        max-width: 1002px;
        position: relative;
        display: flex;
        .search-input {
          :deep(.el-input__inner) {
            height: 81px;
            border: none;
            border-radius: 47px;
            padding-left: 60px;
            padding-right: 150px;
            background: #FFFFFF;
            box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.05), 0px 2px 12px 0px rgba(0,0,0,0.1);
            font-size: 16px;
          }

          :deep(.el-input__prefix) {
            left: 30px;
            font-size: @font-size-note;
            height: 100%;
            display: flex;
            align-items: center;
          }

          :deep(.el-input__suffix) {
            right: 12px;
            height: 100%;
            display: flex;
            align-items: center;
          }

          .search-btn {
            width: 136px;
            height: 58px;
            border-radius: 29px;
            box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.05);
            font-size: 16px;

            background-color: rgba(25, 82, 159, 1);
          }
        }
      }
    }
  }
}
</style> 